import React, { useMemo, useState } from 'react';
import { Chip, Input } from '@heroui/react';
import { Typography } from '../../../../components';
import { SearchInputIc } from '../../../../components/icons';
import { useTranslations } from '../../../../contexts/translation.context';
import { COUNTRY_CONTINENT } from '../../../../utils/country-continent';

interface BlockedCountriesTooltipProps {
  countries: string[];
  offerShortId: string;
  modelUsername: string;
  profileUrl: string;
}

type ContinentCode = 'AF' | 'AN' | 'AS' | 'EU' | 'NA' | 'OC' | 'SA' | 'OTHER';

const CONTINENT_ORDER: ContinentCode[] = ['NA', 'SA', 'EU', 'AS', 'AF', 'OC', 'AN', 'OTHER'];

const createDisplayNames = (locale: string) => {
  try {
    return new Intl.DisplayNames([locale], { type: 'region' });
  } catch {
    return null;
  }
};

const getFlagUrl = (code: string) => {
  if (code.length !== 2) return null;
  return `https://flagcdn.com/16x12/${code.toLowerCase()}.png`;
};

const BlockedCountriesTooltip: React.FC<BlockedCountriesTooltipProps> = ({
  countries,
  offerShortId,
  modelUsername,
  profileUrl,
}) => {
  const { t, lang } = useTranslations();
  const [searchQuery, setSearchQuery] = useState('');

  const locale = useMemo(() => (lang ? lang.split('-')[0] : 'en'), [lang]);
  const primaryDisplay = useMemo(() => createDisplayNames(locale), [locale]);
  const englishDisplay = useMemo(() => createDisplayNames('en'), []);
  const russianDisplay = useMemo(() => createDisplayNames('ru'), []);

  const normalizedCountries = useMemo(() => {
    return countries
      .map((countryCode) => {
        const code = countryCode.toUpperCase();
        const continent = (COUNTRY_CONTINENT[code] ?? 'OTHER') as ContinentCode;
        const primaryName = primaryDisplay?.of(code) ?? code;
        const enName = englishDisplay?.of(code) ?? code;
        const ruName = russianDisplay?.of(code) ?? code;

        return {
          code,
          continent,
          flagUrl: getFlagUrl(code),
          names: {
            primary: primaryName,
            en: enName,
            ru: ruName,
          },
        };
      })
      .sort((a, b) => a.names.primary.localeCompare(b.names.primary));
  }, [countries, primaryDisplay, englishDisplay, russianDisplay]);

  const filteredCountries = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return normalizedCountries;

    return normalizedCountries.filter((country) =>
      [country.names.primary, country.names.en, country.names.ru, country.code]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(query))
    );
  }, [normalizedCountries, searchQuery]);

  const groupedCountries = useMemo(() => {
    const groups: Record<ContinentCode, typeof normalizedCountries> = {
      AF: [],
      AN: [],
      AS: [],
      EU: [],
      NA: [],
      OC: [],
      SA: [],
      OTHER: [],
    };

    filteredCountries.forEach((country) => {
      const targetGroup = groups[country.continent] ? country.continent : 'OTHER';
      groups[targetGroup].push(country);
    });

    return groups;
  }, [filteredCountries]);

  return (
    <div className="flex w-full md:w-[40rem] md:max-w-[40rem] flex-col gap-4 p-4">
      <div className="flex items-start justify-between">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2 ">
            <Typography variant="h6" className="text-auth-ink text-base leading-7 font-medium">
              {t('partner.orders.blockedCountries')}
            </Typography>
            <Chip
              size="sm"
              radius="lg"
              classNames={{
                base: 'bg-auth-surface ',
                content: 'text-sm font-medium !text-auth-muted',
              }}
            >
              {countries.length}
            </Chip>
          </div>
          <div className="flex items-center gap-2">
            <Typography variant="body2" className="text-auth-ink font-medium">
              {offerShortId}
            </Typography>
            <a
              href={profileUrl}
              target="__blank"
              rel="noopener noreferrer"
              className="text-partner text-sm font-medium hover:underline"
            >
              @{modelUsername}
            </a>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <Typography variant="body2" className="text-auth-ink font-medium text-xs">
          {t('partner.orders.blockedCountriesTooltip.countryLabel')}
        </Typography>
        <Input
          type="text"
          placeholder={t('partner.orders.searchCountry')}
          size="sm"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          endContent={<SearchInputIc className="text-neutral-500" />}
          classNames={{
            base: 'w-full',
            input: 'text-sm text-auth-ink placeholder:text-neutral-500',
            inputWrapper: 'rounded-xl h-9 bg-auth-surface border border-transparent',
          }}
        />
      </div>

      <div className="flex max-h-[20rem] flex-col gap-5 overflow-y-auto pr-1 custom-scrollbar">
        {CONTINENT_ORDER.map((continent) => {
          const group = groupedCountries[continent];
          if (!group || group.length === 0) return null;

          return (
            <div key={continent} className="flex flex-col gap-4">
              <Typography variant="body2" className="text-auth-ink !font-medium  !text-sm">
                {t(`partner.orders.countryRegions.${continent}`)}
              </Typography>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-x-4 gap-y-4">
                {group.map((country) => (
                  <div
                    key={country.code}
                    className="flex items-center gap-4 h-4 rounded-lg border border-transparent"
                  >
                    {country.flagUrl ? (
                      <img
                        src={country.flagUrl}
                        alt={country.code}
                        width={16}
                        height={16}
                        className="rounded-1 border border-tin/60"
                      />
                    ) : (
                      <span className="text-sm font-medium text-neutral-500">{country.code}</span>
                    )}
                    <Typography variant="body2" className="text-auth-ink">
                      {country.names.primary}
                    </Typography>
                  </div>
                ))}
              </div>
            </div>
          );
        })}

        {filteredCountries.length === 0 && (
          <div className="rounded-lg border border-tin px-3 py-4 text-center">
            <Typography variant="body2" className="text-auth-muted">
              {t('partner.orders.noCountriesFound')}
            </Typography>
          </div>
        )}
      </div>
    </div>
  );
};

export default BlockedCountriesTooltip;
